<template>
  <div class="calico-field">
    <div
      :class="{
        field: true,
        active,
        error,
        disabled,
      }"
    >
      <span
        v-if="symbol"
        class="body--medium"
      >{{ symbol }}</span>

      <input
        v-if="symbol"
        :value="amount"
        :disabled="disabled"
        class="body--medium"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeyDown"
        @input="onInput"
      >

      <input
        v-else
        ref="inputEl"
        v-bind="filteredAttrs"
        :type="resolvedType"
        :disabled="disabled"
        :value="modelValue"
        class="body--medium"
        @input="$emit('update:modelValue', $event.target.value)"
        @keydown="onKeyDown"
        @focus="active = true"
        @blur="(e) => (!e.target.value ? (active = false) : null)"
      >

      <i
        v-if="showEyeIcon"
        class="bi bi-eye icon--medium sensitive-eye"
        @mousedown.prevent
        @click.stop="toggleSensitive"
      />
      <i
        v-else-if="icon"
        :class="`bi bi-${icon} icon--medium`"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CalicoField',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    symbol: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    onlyNumbers: {
      type: Boolean,
      default: false
    },
    customFormat: {
      type: Function,
      default: null
    },
    type: {
      type: String,
      default: 'text'
    },
    sensitive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      amount: '',
      rawValue: null,
      active: false,
      showSensitive: false
    };
  },
  computed: {
    filteredAttrs() {
      const attrs = { ...this.$attrs };
      delete attrs.type;
      return attrs;
    },
    resolvedType() {
      if (this.sensitive) return this.showSensitive ? 'text' : 'password';
      return this.type;
    },
    showEyeIcon() {
      return this.sensitive;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (!this.active) {
          if (this.customFormat) {
            const result = this.customFormat(val);
            this.amount = result.formatted;
            this.rawValue = val; 
          } else {
            this.amount = val?.toString() || '';
            this.rawValue = val;
          }
        }
      }
    }
  },
  methods: {
    onKeyDown(event) {
      if (this.onlyNumbers) {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
          return;
        }
        const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.', ','];
        if (!/[0-9]/.test(event.key) && !allowed.includes(event.key)) {
          event.preventDefault();
        }
      }
    },
    onInput(event) {
      let raw = event.target.value.replace(/[^\d.,]/g, '');
      const numericString = raw.replace(/,/g, '');
      const parts = numericString.split('.');
      if (parts.length > 2) {
        raw = parts[0] + '.' + parts.slice(1).join('');
      }
      this.amount = raw;
      const parsed = parseFloat(numericString);
      const rounded = isNaN(parsed) ? null : Math.round(parsed);
      if (!isNaN(rounded)) {
        this.rawValue = rounded;
        this.$emit('update:modelValue', rounded);
      } else {
        this.$emit('update:modelValue', null);
      }
    },
    onFocus() {
      this.active = true;
      this.amount = this.rawValue?.toString() || '';
    },
    onBlur() {
      this.active = false;
      const parsed = parseFloat(this.amount.replace(/,/g, ''));
      const value = isNaN(parsed) ? 0 : parsed;
      if (this.customFormat) {
        const result = this.customFormat(value);
        this.amount = result.formatted;
        this.rawValue = result.raw;
        this.$emit('update:modelValue', result.raw);
      } else {
        const rounded = Math.round(value);
        this.amount = rounded.toLocaleString('en-US');
        this.rawValue = rounded;
        this.$emit('update:modelValue', rounded);
      }
    },
    toggleSensitive() {
      const input = this.$refs.inputEl;
      let start = null, end = null;
      if (input && document.activeElement === input) {
        start = input.selectionStart;
        end = input.selectionEnd;
      }
      this.showSensitive = !this.showSensitive;
      this.$nextTick(() => {
        const el = this.$refs.inputEl;
        if (!el) return;
        el.focus();
        if (start !== null && end !== null) {
          try { el.setSelectionRange(start, end); } catch (err) { return; }
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.calico-field {
  display: flex;
  width: 100%;

  .field {
    @include inset-space($space-small);
    @include inline-space($space-xsmall);
    position: relative;
    width: 100%;
    align-items: center;
    color: $color-neutral;
    box-shadow: inset 0 0 0 $border-width-default $color-neutral-light;
    border-radius: $radius-small;
    background: $color-white;
    line-height: $line-height-medium;
  }

  input[type="date"]::-webkit-calendar-picker-indicator {
    position: absolute;
    background: transparent;
    width: 100%;
  }

  input {
    width: 100%;
    height: 100%;
    border: none;
  }

  input:focus {
    outline: none !important;
  }

  span {
    line-height: 0;
    color: $color-neutral !important;
  }

  .active {
    color: $color-black !important;
    box-shadow: inset 0 0 0 $border-width-default $color-border-primary !important;
  }

  .disabled {
    color: $color-text-secondary;
    background: $color-neutral-lightest;
    box-shadow: inset 0 0 0 $border-width-default $color-neutral-lightest;
    
  }

  .error {
    color: $color-semantics-error;
    box-shadow: inset 0 0 0 $border-width-default $color-border-error;

    &>input {
      background: $color-background-error;
    }

    background: $color-background-error;
  }

  .sensitive-eye {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
  }
}
</style>
